import { z } from 'zod';
import brandingJson from '../../../branding.json';

/**
 * What `branding.json` at the repository root must contain. It is the one place an agency
 * name, product name or identifier slug is written down; everything brand-shaped in the
 * worker, the dashboard and the canary script derives from it, so a deployment for another
 * agency changes that file and its logo images rather than any code.
 *
 * @since 1.4.0
 */
export const brandingSchema = z.object({
    /** Lowercase kebab-case identifier every header, cookie, tag and path derives from. */
    slug: z.string().regex(/^[a-z][a-z0-9]*(-[a-z0-9]+)*$/, 'The slug is lowercase kebab-case'),
    agency: z.object({
        name: z.string().min(1),
        shortName: z.string().min(1),
        url: z.url(),
    }),
    product: z.object({
        name: z.string().min(1),
        /** The two words of the typographic fallback the brand mark shows without a logo. */
        wordmark: z.tuple([z.string().min(1), z.string().min(1)]),
    }),
    plugin: z.object({
        /** The site-side WordPress plugin that carries the QC-mode module. */
        name: z.string().min(1),
    }),
    qc: z.object({
        /** The monitoring mailbox the plugin copies canary notifications to; the plugin ships the same value. */
        recipient: z.email(),
    }),
});

export type Branding = z.infer<typeof brandingSchema>;

/**
 * The branding in effect for this build, validated once at import so a malformed file fails
 * every workspace at startup rather than one screen at a time.
 *
 * @since 1.4.0
 */
export const BRANDING: Branding = brandingSchema.parse(brandingJson);

/**
 * The slug in snake case, for names that cannot carry a hyphen such as cookies and form fields.
 *
 * @since 1.4.0
 */
export const BRAND_SLUG_SNAKE = BRANDING.slug.replace(/-/g, '_');

/**
 * The session cookie name per environment. The `__Host-` prefix demands Secure and a root
 * path, which is right in production but awkward over plain http in local development.
 *
 * @since 1.4.0
 */
export const SESSION_COOKIE_NAMES = {
    production: `__Host-${BRAND_SLUG_SNAKE}_session`,
    development: `${BRAND_SLUG_SNAKE}_session`,
} as const;

/**
 * Where the site-side plugin serves its form inventory, relative to the site root.
 *
 * @since 1.4.0
 */
export const PLUGIN_INVENTORY_PATH = `/wp-json/${BRANDING.slug}/v1/inventory`;

/**
 * Tag marking every Checkly check this dashboard provisions, whichever deployment did it.
 *
 * @since 1.4.0
 */
export const CHECKLY_OWNED_TAG = BRANDING.slug;

/**
 * Default names for the AWS resources an SES account provisions, overridable per environment
 * through Worker vars so two deployments in one AWS account cannot collide.
 *
 * @since 1.4.0
 */
export const SES_RESOURCE_DEFAULTS = {
    configurationSet: BRANDING.slug,
    topicName: `${BRANDING.slug}-mail-events`,
    logGroup: `/aws/events/${BRANDING.slug}-ses`,
    ruleName: `${BRANDING.slug}-ses-events`,
} as const;

/**
 * The name the canary types into a form's text fields. It has to be recognisable as a test
 * by whoever sees the entry on the client's site, so it carries the product name.
 *
 * @since 1.4.0
 */
export const CANARY_TEST_NAME = `${BRANDING.product.name} Test`;

/**
 * The message the canary types into a form's textarea fields.
 *
 * @since 1.4.0
 */
export const CANARY_TEST_MESSAGE =
    `${BRANDING.product.name.toUpperCase()} TEST: automated monitoring check. ` + 'Please do not action or reply.';
